// Global UI store: cross-cutting UI state (rail, base imagery, chart overlay,
// time scrubbers, glide-ring, deep-link slots, plate-overlay state, etc.).
//
// Layer visibility has moved to layerVisibilityStore.ts (HI-2 step 2d).

import { create } from 'zustand';
import type { BaseImageryId, ChartOverlayId, BaseLayerId } from '../map/sources';
import type { PlateOverlaySpec } from '../lib/plateGeoref';
// Persistence + cloud-sync helpers were extracted to prefsSync.ts as
// the first slice of HI-2 (audit 2026-05-22). The store calls these
// helpers but no longer owns the localStorage / Supabase code.
import {
  loadUiPrefs,
  persistUiPrefs,
  schedulePushUiPrefs,
  bootstrapAuthSyncWatcher,
  type PersistedUiPrefs,
} from './prefsSync';
import { useLayerVisibilityStore } from './layerVisibilityStore';
// Re-export so AccountSection's Force re-sync button keeps its import.
export { schedulePushUiPrefs };

/**
 * Back-compat alias. Old call sites import `BaseLayer` and pass things
 * like 'sectional' — those still work via setBaseLayerLegacy() which
 * splits the legacy id into the new (imagery, chart) tuple.
 */
export type BaseLayer = BaseLayerId;

interface UiState {
  railOpen: boolean;
  toggleRail: (next?: boolean) => void;

  /** Base imagery (always one). 'basic' = dim OSM, 'street' = full OSM, 'satellite' = Esri imagery. */
  baseImagery: BaseImageryId;
  setBaseImagery: (id: BaseImageryId) => void;

  /** Optional aeronautical chart overlay layered on top of imagery. */
  chartOverlay: ChartOverlayId;
  setChartOverlay: (id: ChartOverlayId) => void;

  /**
   * Chart-overlay opacity (0–1). Drops below 1 so MapLibre's painter
   * blends the imagery layer beneath. Lower values let more imagery
   * through; higher values make the chart dominate. Sweet spot for
   * 'sectional over satellite' is ~0.85; for chart-only feel use 0.99.
   * Default 0.9 — see comment in `style.ts` for the empirical reason
   * we don't go all the way to 1.0.
   */
  chartOpacity: number;
  setChartOpacity: (v: number) => void;

  /**
   * Legacy single-base setter. Splits old ids into the new tuple so
   * pre-refactor call sites keep working. Deprecated.
   */
  baseLayer: BaseLayer;
  setBaseLayer: (l: BaseLayer) => void;

  /**
   * Hazard time-scrubber offset, in whole hours from "now" (UTC).
   * 0 = current; positive = future forecast horizon; negative = look
   * back at recently-aged G-AIRMETs. Range -3..+9 in 1h steps.
   *
   * MapView reads this and rebuilds GFA layer filters so polygons
   * whose `validTime` is within ±3h of (now + offset) render. SIGMETs
   * / CWAs are filtered against their own validTimeFrom /
   * validTimeTo overlap with the slider window, so the user can
   * scrub through 'expiring soon' and 'about to start' nowcasts the
   * same way.
   */
  wxHazardOffsetH: number;
  setWxHazardOffsetH: (h: number) => void;

  /**
   * NEXRAD time-scrubber offset, in WHOLE MINUTES from now (UTC). Always
   * non-positive (radar is observation, not forecast). Range -60..0 in
   * 5-minute steps. 0 = current frame; -60 = the frame from one hour ago.
   *
   * MapView reads this and re-issues setTiles() on the nexrad source
   * with the appropriate Iowa State Mesonet URL template.
   */
  nexradOffsetMin: number;
  setNexradOffsetMin: (m: number) => void;

  /**
   * Radar product variant (boss request 2026-05-21 19:42 PDT, mirroring
   * ForeFlight's three radar layers):
   *   'classic'   NEXRAD Level III base reflectivity (default — our
   *               original behavior). Supports the time scrubber.
   *   'composite' MRMS multi-sensor composite reflectivity (current only).
   *   'lowtilt'   MRMS Seamless Hybrid-Scan Reflectivity (current only).
   * Persisted to localStorage.
   */
  nexradVariant: 'classic' | 'composite' | 'lowtilt';
  setNexradVariant: (v: 'classic' | 'composite' | 'lowtilt') => void;

  /**
   * Surface Analysis time-scrubber offset, in WHOLE HOURS forward of
   * the current WPC analysis. WPC publishes the analysis + a stack of
   * PMSL forecasts at +6/+12/+18/+24/+30/+36/+48/+60h plus Day-3..7
   * (72/96/120/144/168). 0 = current analysis (default).
   *
   * The set of valid steps is:
   *   [0, 6, 12, 18, 24, 30, 36, 48, 60, 72, 96, 120, 144, 168]
   * MapView snaps to the closest valid step on slider change.
   */
  sfcAnalysisOffsetH: number;
  setSfcAnalysisOffsetH: (h: number) => void;

  /**
   * Forecast offset (hours) for the HRRR-Smoke layer. The ingest cron
   * publishes one PNG per hour in `SMOKE_FORECAST_HOURS`. The smoke
   * layer keeps its own state separate from `wxHazardOffsetH` so the
   * AIRMET/SIGMET/CWA ±3h scrubber doesn't drag the smoke forecast,
   * and vice-versa. Phase 1 ships [0, 3, 6, 12, 18].
   *
   * Default 0 ("now"). SmokeBar slider snaps to the nearest published
   * step on each change.
   */
  smokeOffsetH: number;
  setSmokeOffsetH: (h: number) => void;

  /**
   * Reusable altitude slider state. Right-hand vertical slider that
   * applies to any layer needing an altitude reference — dynamic TAWS,
   * G-AIRMET icing/turbulence altitude bands, freezing level, etc.
   *
   * altitudeSliderFt: current slider value in feet MSL.
   * altitudeSliderLocked: when true, the slider auto-follows the live
   *   GPS altitude (the slider becomes a passive readout); when false,
   *   the user is free to drag it to query 'what would the hazards
   *   look like if I were at this altitude?'.
   */
  altitudeSliderFt: number;
  altitudeSliderLocked: boolean;
  setAltitudeSliderFt: (ft: number) => void;
  setAltitudeSliderLocked: (locked: boolean) => void;

  /**
   * Map display toggles formerly in the Layers sheet — these are
   * personal-preference rather than data layers, so they live in
   * Settings. They mirror the visibility state in `layers.breadcrumbs`
   * and `layers.rangeRings` for MapView, but the user-facing UI is
   * in /settings only.
   */
  showBreadcrumbs: boolean;
  showRangeRings: boolean;
  setShowBreadcrumbs: (v: boolean) => void;
  setShowRangeRings: (v: boolean) => void;

  /**
   * 2026-05-16 day-1 spike: enable 3D airspace + TFR + SUA volumes via
   * MapLibre native fill-extrusion. When ON and the map is pitched, Class
   * B/C/D become wedding cakes, TFRs and SUA become volumetric.
   *
   * Gated behind a flag so iPad PWA perf is opt-in and the entire feature
   * can be ripped out by setting this false + deleting `src/3d/` if it
   * doesn't pan out.
   *
   * Persisted to ui_prefs so the preference survives reload + sync’s across
   * devices.
   */
  enable3d: boolean;
  setEnable3d: (v: boolean) => void;

  /** Sub-filter on the heliports layer: when true, only render NASR
   *  heliports whose facility name matches the hospital regex (see
   *  api/bbox.ts isHospitalHeliport). Wiley HEMS-ops request
   *  2026-05-13. Default false. Heliports layer must also be ON for
   *  this to have effect. */
  heliportsHospitalsOnly: boolean;
  setHeliportsHospitalsOnly: (v: boolean) => void;

  /**
   * Glide-ring — safety buffer above terrain at touchdown (ft). The
   * computed reach ends when the glide-altitude line drops below
   * terrain + this buffer. Default 100 ft per boss.
   */
  glideRingSafetyBufferFt: number;
  setGlideRingSafetyBufferFt: (v: number) => void;

  /**
   * Glide-ring — emergency mode. When ON, the map auto-zooms to the
   * glide-ring extent, the ring renders in a brighter color, and
   * airports inside the ring are highlighted. Toggled via the
   * floating "Emergency Glide" button on the map. Never persisted.
   */
  glideRingEmergencyMode: boolean;
  setGlideRingEmergencyMode: (v: boolean) => void;

  /**
   * Glide-ring — set by GlideRingLayer when ownship is on (or near)
   * the ground. The UI uses this to surface a 'On ground — no glide
   * range' chip instead of silently rendering an invisible polygon.
   * Not persisted.
   */
  glideRingOnGround: boolean;
  setGlideRingOnGround: (v: boolean) => void;

  /**
   * Auto-record a track log on takeoff. When ON, the airborne
   * detector starts a fresh track at the moment we transition
   * ground → airborne and (optionally) stops it when we transition
   * airborne → ground.
   */
  autoRecordTrackOnTakeoff: boolean;
  autoStopTrackOnLanding: boolean;
  setAutoRecordTrackOnTakeoff: (v: boolean) => void;
  setAutoStopTrackOnLanding: (v: boolean) => void;

  /**
   * Pending airport-detail request. Set by non-map pages (e.g. the
   * Airports list overlay) when the user picks an airport: MapPage
   * consumes this slot on mount, recenters the map on the airport,
   * opens the rich AirportDetail panel, then clears the slot. This
   * lets us route an in-app navigation like "Airports → tap KSIY"
   * back into the persistent map experience instead of stranding the
   * user on a separate full-page route.
   */
  pendingAirport: { ident: string; lat: number; lon: number; name: string } | null;
  setPendingAirport: (a: { ident: string; lat: number; lon: number; name: string } | null) => void;

  /**
   * One-shot deep-link payload consumed by ProcedureTab.
   * Set by AirportsPage when navigating from the plate-corrections queue
   * (/airports/:ident?openPlate=X.PDF&autoEditor=1). MapPage opens
   * AirportDetail via the normal pendingAirport slot; AirportDetail reads
   * this slot on mount to switch to the Procedure tab and auto-focus +
   * optionally auto-launch the correction editor for the named plate.
   * Cleared after first consumption to prevent re-trigger on refresh.
   */
  pendingPlateOpen: { pdfName: string; autoEditor: boolean } | null;
  setPendingPlateOpen: (v: { pdfName: string; autoEditor: boolean } | null) => void;

  // ── Approach plate overlay ─────────────────────────────────────────
  /**
   * When non-null, PlateOverlayLayer renders this plate as a translucent
   * raster overlay on the moving map. The PlateViewer "View on Map" button
   * sets this; the floating PlateOverlayControls demo button sets this with
   * the KMFR R14Y demo spec.
   */
  activePlateOverlay: PlateOverlaySpec | null;
  setPlateOverlay: (spec: PlateOverlaySpec | null) => void;

  /** Raster opacity for the plate overlay (0–1). Default 0.75. */
  plateOverlayOpacity: number;
  setPlateOverlayOpacity: (v: number) => void;

  /**
   * When true, plate overlays render the FULL plate page (top header,
   * minima, profile, plan-view) using the server-provided `pageCorners`.
   * When false (legacy default), the overlay is cropped to the plan-view
   * section using `planViewRect`. Plan-view shows the actual approach
   * geometry over the map cleanly; full-page shows everything the chart
   * publishes including DA/MDA/profile, but is harder to read at small
   * zooms. Default: true (boss prefers seeing the full chart).
   */
  plateOverlayShowFullPage: boolean;
  setPlateOverlayShowFullPage: (v: boolean) => void;

  /**
   * When true (default), render ownship aircraft icon + active flight plan
   * route as vector overlays on top of the plate raster when a georef'd
   * plate overlay is active and status === 'ok'. Toggled by the
   * PlateOverlayControls "Show ownship + route on plate" switch.
   * Persisted to localStorage so the preference survives page reloads.
   */
  plateOverlayShowOwnship: boolean;
  setPlateOverlayShowOwnship: (v: boolean) => void;

  /**
   * Resolved georef result for the currently-active plate overlay.
   * Written by PlateOverlayLayer after resolveCorners() completes; read by
   * PlateOverlayControls so it can render a cockpit-awareness banner when
   * the plate is low-confidence or the server-side georef failed and we
   * had to fall back to the CIFP-bbox bounding box (much less accurate).
   * Null while resolving, or when no overlay is active.
   */
  plateOverlayResult: {
    status: 'ok' | 'low-confidence' | 'fallback-cifp' | 'spec-corners';
    residualNm: number | null;
  } | null;
  setPlateOverlayResult: (
    r:
      | {
          status: 'ok' | 'low-confidence' | 'fallback-cifp' | 'spec-corners';
          residualNm: number | null;
        }
      | null,
  ) => void;

  /**
   * One-shot map jump for incidents (and other non-airport targets).
   * When the user taps "Show on map" on a WFIGS search row in
   * /incidents, that page sets this slot and navigates to /map; the
   * map picks it up, recenters with a zoom appropriate for the
   * acreage, and clears the slot. `irwinId` is optional — if set the
   * map can also flash a focus ring on the matching fire perimeter
   * feature (NIFS perimeter layer is keyed by IRWIN ID).
   */
  pendingMapJump: {
    lat: number;
    lon: number;
    /** Approximate target zoom. Caller decides based on size. */
    zoom: number;
    /** Display name for a transient "focused on X" banner. */
    label?: string;
    /** IRWIN ID for matching the fire-perimeter polygon. */
    irwinId?: string | null;
  } | null;
  setPendingMapJump: (a: {
    lat: number;
    lon: number;
    zoom: number;
    label?: string;
    irwinId?: string | null;
  } | null) => void;

  /**
   * Follow-ownship mode. When ON, the map pans to keep the GPS fix
   * centered. Automatically engaged when the airborne detector fires
   * a takeoff transition. Disengages on manual pan/zoom by the user.
   * Re-engaged by tapping the crosshair button in MapTopBar.
   */
  followOwnship: boolean;
  setFollowOwnship: (v: boolean) => void;

  // Wind-particle layer prefs extracted to windPrefsStore (HI-2 step 2c).
  // See src/stores/windPrefsStore.ts.
}

/**
 * Translate a legacy single-id baseLayer into the new (imagery, chart)
 * tuple. Used to keep `setBaseLayer('sectional')` working until every
 * caller is migrated.
 */
function splitLegacyBase(l: BaseLayer): { imagery: BaseImageryId; chart: ChartOverlayId } {
  switch (l) {
    case 'sectional':
    case 'helo':
    case 'ifr-low':
    case 'ifr-high':
      return { imagery: 'basic', chart: l };
    case 'topo':
      return { imagery: 'topo', chart: 'none' };
    case 'osm':
      return { imagery: 'street', chart: 'none' };
    case 'satellite':
      return { imagery: 'satellite', chart: 'none' };
    case 'basic':
      return { imagery: 'basic', chart: 'none' };
    case 'street':
      return { imagery: 'street', chart: 'none' };
    default:
      return { imagery: 'basic', chart: 'sectional' };
  }
}

// Hydrate persisted preferences once at module load so the first
// render already reflects them (no flicker / no default-then-snap).
const PERSISTED_PREFS = loadUiPrefs();

// Traffic-prefs migration ladder has moved to trafficPrefsStore.ts
// Instrument-prefs (including clock24h init) have moved to instrumentPrefsStore.ts
// (HI-2 step 2a). That module runs the v0→v1→v2 migration at load
// time before the store reads persisted values.

export const useUiStore = create<UiState>((set, get) => ({
  railOpen: PERSISTED_PREFS.railOpen ?? false,
  toggleRail: (next) => {
    const v = next ?? !get().railOpen;
    set({ railOpen: v });
    persistUiPrefs({ railOpen: v });
  },

  baseImagery: PERSISTED_PREFS.baseImagery ?? 'basic',
  setBaseImagery: (id) => {
    set({ baseImagery: id, baseLayer: id as BaseLayer });
    persistUiPrefs({ baseImagery: id });
  },

  chartOverlay: PERSISTED_PREFS.chartOverlay ?? 'sectional',
  setChartOverlay: (id) => {
    set({
      chartOverlay: id,
      baseLayer: (id === 'none' ? get().baseImagery : id) as BaseLayer,
    });
    persistUiPrefs({ chartOverlay: id });
  },

  chartOpacity: PERSISTED_PREFS.chartOpacity ?? 0.9,
  setChartOpacity: (v) => {
    const clamped = Math.max(0, Math.min(1, v));
    set({ chartOpacity: clamped });
    persistUiPrefs({ chartOpacity: clamped });
  },

  baseLayer: 'sectional',
  setBaseLayer: (l) => {
    const { imagery, chart } = splitLegacyBase(l);
    set({ baseLayer: l, baseImagery: imagery, chartOverlay: chart });
    persistUiPrefs({ baseImagery: imagery, chartOverlay: chart });
  },

  // showInstruments, gloveMode, keepScreenAwake, highContrastInstruments,
  // instruments, clock24h + their setters have moved to instrumentPrefsStore
  // (HI-2 step 2b).

  // Default = 0 ("now"). G-AIRMETs reissue every 6h with a single
  // forecast=3 frame, so the closest match for "now" is the
  // currently-active +3h frame. The MapView filter widens the slider
  // by ±3h so the user always sees something on the map at the
  // default position.
  wxHazardOffsetH: 0,
  setWxHazardOffsetH: (h) => set({ wxHazardOffsetH: h }),

  // NEXRAD scrubber default = 0 (current frame). Past frames at 5-min
  // increments back to -60.
  nexradOffsetMin: 0,
  setNexradOffsetMin: (m) => set({ nexradOffsetMin: m }),

  // Radar variant default = 'classic' (preserves previous behavior).
  nexradVariant: PERSISTED_PREFS.nexradVariant ?? 'classic',
  setNexradVariant: (v) => {
    set({ nexradVariant: v });
    persistUiPrefs({ nexradVariant: v });
  },

  sfcAnalysisOffsetH: 0,
  setSfcAnalysisOffsetH: (h) => set({ sfcAnalysisOffsetH: h }),

  smokeOffsetH: PERSISTED_PREFS.smokeOffsetH ?? 0,
  setSmokeOffsetH: (h) => {
    set({ smokeOffsetH: h });
    persistUiPrefs({ smokeOffsetH: h });
  },

  // Default altitude slider state: 5000 ft MSL (above most CONUS
  // terrain), locked to GPS so the slider auto-tracks the aircraft
  // as soon as a fix arrives.
  altitudeSliderFt: 5000,
  altitudeSliderLocked: true,
  // Cap at 25,000 ft (FL250). Above that there's no meaningful
// terrain on Earth (Mt. Everest ~29,000 ft) and the terrain advisor
// reads as 'unrestricted' for the whole drag range. Lowered from
// 45,000 ft 2026-05-24.
setAltitudeSliderFt: (ft) => set({ altitudeSliderFt: Math.max(0, Math.min(25_000, Math.round(ft))) }),
  setAltitudeSliderLocked: (locked) => set({ altitudeSliderLocked: locked }),

  // Traffic prefs have moved to trafficPrefsStore (HI-2 step 2a).
  showBreadcrumbs: PERSISTED_PREFS.showBreadcrumbs ?? true,
  showRangeRings: PERSISTED_PREFS.showRangeRings ?? true,

  /** 2026-05-16 spike: off by default. User must opt in via Aero panel. */
  enable3d: PERSISTED_PREFS.enable3d ?? false,
  autoRecordTrackOnTakeoff: PERSISTED_PREFS.autoRecordTrackOnTakeoff ?? false,
  autoStopTrackOnLanding: PERSISTED_PREFS.autoStopTrackOnLanding ?? false,
  heliportsHospitalsOnly: PERSISTED_PREFS.heliportsHospitalsOnly ?? false,
  glideRingSafetyBufferFt: PERSISTED_PREFS.glideRing?.safetyBufferFt ?? 100,
  glideRingEmergencyMode: false,
  glideRingOnGround: false,
  // Traffic setters have moved to trafficPrefsStore (HI-2 step 2a).
  // Instrument/display pref setters have moved to instrumentPrefsStore (HI-2 step 2b).
  setShowBreadcrumbs: (v) => {
    // Mirror into layerVisibilityStore so MapView's layers selector
    // keeps working without a new code path.
    const layers = { ...useLayerVisibilityStore.getState().layers, breadcrumbs: v };
    set({ showBreadcrumbs: v });
    useLayerVisibilityStore.setState({ layers });
    persistUiPrefs({ showBreadcrumbs: v, layers });
  },
  setHeliportsHospitalsOnly: (v) => {
    set({ heliportsHospitalsOnly: v });
    persistUiPrefs({ heliportsHospitalsOnly: v });
  },
  setGlideRingSafetyBufferFt: (v) => {
    const clamped = Math.max(0, Math.min(2000, Math.round(v)));
    set({ glideRingSafetyBufferFt: clamped });
    persistUiPrefs({ glideRing: { safetyBufferFt: clamped } });
  },
  setGlideRingEmergencyMode: (v) => {
    set({ glideRingEmergencyMode: v });
  },
  setGlideRingOnGround: (v) => {
    if (useUiStore.getState().glideRingOnGround === v) return;
    set({ glideRingOnGround: v });
  },

  setShowRangeRings: (v) => {
    // Mirror into layerVisibilityStore so MapView's layers selector
    // keeps working without a new code path.
    const layers = { ...useLayerVisibilityStore.getState().layers, rangeRings: v };
    set({ showRangeRings: v });
    useLayerVisibilityStore.setState({ layers });
    persistUiPrefs({ showRangeRings: v, layers });
  },

  setEnable3d: (v) => {
    if (useUiStore.getState().enable3d === v) return;
    set({ enable3d: v });
    persistUiPrefs({ enable3d: v });
  },
  setAutoRecordTrackOnTakeoff: (v) => {
    set({ autoRecordTrackOnTakeoff: v });
    persistUiPrefs({ autoRecordTrackOnTakeoff: v });
  },
  setAutoStopTrackOnLanding: (v) => {
    set({ autoStopTrackOnLanding: v });
    persistUiPrefs({ autoStopTrackOnLanding: v });
  },

  pendingAirport: null,
  setPendingAirport: (a) => set({ pendingAirport: a }),

  pendingPlateOpen: null,
  setPendingPlateOpen: (v) => set({ pendingPlateOpen: v }),

  // ── Approach plate overlay ─────────────────────────────────────────
  activePlateOverlay: null,
  setPlateOverlay: (spec) =>
    set({
      activePlateOverlay: spec,
      // Clear stale result so the banner doesn't flash on the new plate
      // before the layer's resolveCorners() finishes.
      plateOverlayResult: spec ? null : null,
    }),
  plateOverlayOpacity: 0.75,
  setPlateOverlayOpacity: (v) => set({ plateOverlayOpacity: Math.max(0, Math.min(1, v)) }),
  plateOverlayShowFullPage: true,
  setPlateOverlayShowFullPage: (v) => set({ plateOverlayShowFullPage: !!v }),
  plateOverlayShowOwnship: (() => {
    try {
      const raw = localStorage.getItem('fyrefly:plate-overlay-show-ownship');
      return raw === null ? true : raw !== 'false';
    } catch { return true; }
  })(),
  setPlateOverlayShowOwnship: (v) => {
    try { localStorage.setItem('fyrefly:plate-overlay-show-ownship', String(!!v)); } catch { /* quota */ }
    set({ plateOverlayShowOwnship: !!v });
  },
  plateOverlayResult: null,
  setPlateOverlayResult: (r) => set({ plateOverlayResult: r }),

  pendingMapJump: null,
  setPendingMapJump: (a) => set({ pendingMapJump: a }),

  followOwnship: false,
  setFollowOwnship: (v) => set({ followOwnship: v }),

  // Wind-particle layer prefs extracted to windPrefsStore (HI-2 step 2c).
  // See src/stores/windPrefsStore.ts.
}));


// ── Cloud-sync bootstrap ────────────────────────────────────────────
//
// Wires Supabase pull-on-sign-in. The persistence logic lives in
// prefsSync.ts (extracted as the first slice of HI-2). We pass our
// own setState so prefsSync can write back into the store without
// taking a hard dependency on this file (avoids a circular import).
// Layer visibility is now applied directly to layerVisibilityStore
// inside prefsSync (HI-2 step 2d).
bootstrapAuthSyncWatcher(
  (patch) => useUiStore.setState(patch),
);
